/** @format */

import { AnalyticsService, HttpService, WINDOW, Logger } from '@shared/data-access/services';
import { Chain, Environment, HomrAudiences } from '@shared/data-access/interfaces';
import {
  DOCUMENT,
  Injectable,
  PLATFORM_ID,
  TransferState,
  inject,
  makeStateKey,
} from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, map, retry } from 'rxjs/operators';

import { CustomHttpHeaders } from '@shared/data-access/enums';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { HomrPage, HomrResponse } from '../interfaces/homr-page.interface';
import { isPlatformServer } from '@angular/common';
import { Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class HomrDataService {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly chain = inject(Chain);
  private readonly environment = inject(Environment);
  private readonly http = inject(HttpClient);
  private readonly httpService = inject(HttpService);
  private readonly state = inject(TransferState);
  private readonly document: Document = inject(DOCUMENT);
  private readonly logger = inject(Logger);
  protected readonly platformId: object = inject(PLATFORM_ID);
  protected readonly window: Window = inject(WINDOW);

  private readonly homRFilterKey = makeStateKey<string>('homR_SEOToken');

  // List of items that the stateKey should not be set for, to force loading in the browser
  private readonly exceptionList = DCSG_CONSTANTS.homr.stateKeyExceptions;
  private readonly aosList = DCSG_CONSTANTS.homr.aosPageTypes;
  private readonly isSinglePage = (variableToCheck: unknown): variableToCheck is HomrPage =>
    (variableToCheck as HomrPage).audience !== undefined;

  /**
   * @description
   * The main call to retrieve page data from homR
   *
   * @param pageType
   * @param retries
   * @param pageName
   * @param normalized
   * @param noSelection
   * @returns - {homR page object}
   */
  getData$(
    pageType: string,
    retries = 2,
    pageName?: string,
    normalized = false,
    noSelection = false,
  ) {
    pageName =
      this.document.body.classList.contains('aos') && this.aosList.includes(pageType)
        ? 'aos'
        : pageName
          ? pageName
          : '';
    return this.getTemplateData$(pageType, retries, normalized, noSelection, pageName);
  }

  /**
   * @description
   * Wrapper for consumers that expect a single HOMR page object.
   * Returns null when the API returns an array or null.
   */
  getDataPage$(
    pageType: string,
    retries = 2,
    pageName?: string,
    normalized = false,
    noSelection = false,
  ): Observable<HomrPage | null> {
    return this.getData$(pageType, retries, pageName, normalized, noSelection).pipe(
      map((response) => (response && !Array.isArray(response) ? response : null)),
    );
  }

  /**
   * @description
   * Wrapper for consumers that expect a list of HOMR pages.
   * Normalizes single-page and null responses into an array.
   */
  getDataList$(
    pageType: string,
    retries = 2,
    pageName?: string,
    normalized = false,
    noSelection = false,
  ): Observable<HomrPage[]> {
    return this.getData$(pageType, retries, pageName, normalized, noSelection).pipe(
      map((response) => {
        if (Array.isArray(response)) {
          return response;
        }

        if (response) {
          return [response];
        }

        return [];
      }),
    );
  }

  getAemData$(url: string) {
    return this.http.get(
      `${window.location.href.includes('localhost') ? this.chain.metaCanonical : ''}${
        this.environment.api.aem?.proxy?.path ?? ''
      }${url}`,
      { responseType: 'text' },
    );
  }

  getAemDataObject$() {
    // to be updated as part of https://dcsgcloud.atlassian.net/browse/SPPJ-87
    return this.http.get(
      `${window.location.href.includes('localhost') ? this.chain.metaCanonical : ''}${
        this.environment.api.aemFoundationDetails?.proxy?.path ?? ''
      }`,
    );
  }

  /**
   * @description
   * The fallback call to get the default content if nothing was returned in the element call
   *
   * @param pageType
   * @returns - {homR page object}
   */
  getDefault$(pageType: string, retries = 0) {
    const pageName = this.document.body.classList.contains('aos') ? 'aos' : 'default';
    return this.getTemplateData$(pageType, retries, false, false, pageName);
  }

  /**
   * @description
   * Wrapper for consumers that expect a single fallback HOMR page object.
   */
  getDefaultPage$(pageType: string, retries = 0): Observable<HomrPage | null> {
    return this.getDefault$(pageType, retries).pipe(
      map((response) => (response && !Array.isArray(response) ? response : null)),
    );
  }

  /**
   * @description
   * Wrapper for consumers that expect fallback HOMR pages as a list.
   */
  getDefaultList$(pageType: string, retries = 0): Observable<HomrPage[]> {
    return this.getDefault$(pageType, retries).pipe(
      map((response) => {
        if (Array.isArray(response)) {
          return response;
        }

        if (response) {
          return [response];
        }

        return [];
      }),
    );
  }

  /**
   * @description
   * DO NOT CALL THIS ONE DIRECTLY, CALL getData$
   * This is the actual API calls out to dAPI
   *
   * @param pageType
   * @param retries
   * @param normalized
   * @param noSelection
   * @param pageName
   * @returns - {homR page object}
   */
  private getTemplateData$(
    pageType: string,
    retries: number,
    normalized: boolean,
    noSelection: boolean,
    pageName?: string,
  ): Observable<HomrResponse> {
    const finalPageName = pageName || this.getPageName();

    const ELEMENT_KEY = makeStateKey<HomrResponse>(this.createStateKey(pageType, finalPageName));
    const element = this.state.get(ELEMENT_KEY, null);

    // Check to see if state exists
    if (element) {
      // Return the existing state
      return of(element);
    }

    // Set http parameters
    let httpParams = new HttpParams()
      .set('pageType', pageType)
      .set('name', finalPageName)
      .set('chain', pageType !== 'size-chart' ? this.chain.chainMarketingIdentifierAbbr : 'dsg');

    // If normalized
    if (normalized) {
      httpParams = httpParams.append('normalize', `${normalized}`);
    }

    // If noSelection
    if (noSelection) {
      httpParams = httpParams.append('noSelection', `${noSelection}`);
    }

    let httpHeaders = new HttpHeaders(this.getHeaders());

    // Custom HTTP Headers
    httpHeaders = httpHeaders.set(CustomHttpHeaders.X_PROXY_REQUEST, 'true');

    // Check to see if there is a timeout specified
    if (isPlatformServer(this.platformId)) {
      // Set the timeout header
      httpHeaders = httpHeaders.set(CustomHttpHeaders.X_TIMEOUT, `${DCSG_CONSTANTS.homr.timeout}`);
    }

    // Check if page type is size-chart and adjust the timeout
    if (pageType === 'size-chart') {
      httpHeaders = httpHeaders.set(
        CustomHttpHeaders.X_TIMEOUT,
        `${DCSG_CONSTANTS.timeouts.default}`,
      );
    }

    // HTTP Options
    const httpOptions = {
      headers: httpHeaders,
      params: httpParams,
      transferCache: !this.exceptionList.includes(pageType),
    };

    const baseDomain = this.isServerAndBaseDomainSet()
      ? this.environment.api.homr.baseDomain?.[this.chain.chainIdentifierAbbr]
      : '';
    const proxyPath = this.environment.api.homr?.proxy?.path;

    if (!proxyPath) {
      return of(null);
    }

    return this.http
      .get<HomrPage | HomrPage[]>(`${baseDomain}${proxyPath}v4/content`, httpOptions)
      .pipe(
        retry(retries),
        catchError((error) => {
          this.logger.error(
            `HOMR_DATA_ERROR: Error fetching data for pageType: ${pageType}`,
            error,
          );
          this.analyticsService.generateLogError(
            'HOMR_DATA_ERROR',
            `Error fetching data for pageType: ${pageType}`,
            error,
            { pageType, retries },
          );
          return of(null);
        }),
        map((response: HomrResponse) => {
          let result = response;
          if (
            (noSelection && Array.isArray(response) && !response.length) ||
            (response && !Array.isArray(response) && response.content === undefined)
          ) {
            result = null;
          }

          if (!this.exceptionList.includes(pageType)) {
            this.state.set(ELEMENT_KEY, result);
          }

          if (this.environment.feature.homRAudiences[this.chain.chainIdentifierAbbr]) {
            this.analyticsService.setHomrAudiences(this.buildAudienceObject(result));
          }

          return result;
        }),
      );
  }

  setPageName(seoToken: string) {
    this.state.set(this.homRFilterKey, seoToken);
  }

  private getPageName(isHome = false) {
    const nameState = this.state.get(this.homRFilterKey, null);
    let pageName = '';

    if (isHome) {
      pageName = 'homepage';
    } else if (nameState) {
      pageName = nameState;
    }

    return pageName;
  }

  private createStateKey(pageType: string, pageName: string): string {
    const previewDate = this.getHeaders()['previewdate'];
    return previewDate ? `${pageType}-${pageName}-${previewDate}` : `${pageType}-${pageName}`;
  }

  private getHeaders(): Record<string, string> {
    const httpHeaders = {
      'X-TIMEOUT': '3000',
      'Content-Type': 'application/json',
    };

    const acceptedHeaders = DCSG_CONSTANTS.homr.acceptedHeaders;

    const headerObj = this.findHeaders(acceptedHeaders);

    return { ...httpHeaders, ...headerObj };
  }

  private findHeaders(headerList: string[]) {
    const headerObj: Record<string, string> = {};

    for (const header of headerList) {
      const headerVal = this.httpService.getRequestHeader(header);
      if (headerVal) {
        headerObj[header] = headerVal;
      }
    }
    return headerObj;
  }

  private buildAudienceObject(response: HomrResponse): HomrAudiences[] {
    const audienceAnalytics: HomrAudiences[] = [];
    const defaultAudience = 'default';
    const setAudience = (pageType: string, audience: string, content?: string): HomrAudiences => ({
      pageType,
      audience,
      ...(content && { content }),
    });

    if (response !== null && this.isSinglePage(response)) {
      const pageType = response.requestContext?.pageType;
      if (!pageType) {
        return audienceAnalytics;
      }

      if (response.audience !== undefined && response.audience !== defaultAudience) {
        // Push template audience
        audienceAnalytics.push(setAudience(pageType, response.audience));
      }

      for (const content in response.contentScheduleMeta) {
        // Find the content audiences
        if (
          response.contentScheduleMeta[content].audience !== undefined &&
          response.contentScheduleMeta[content].audience !== defaultAudience
        ) {
          audienceAnalytics.push(
            setAudience(pageType, response.contentScheduleMeta[content].audience, content),
          );
        }
      }
    }
    return audienceAnalytics;
  }

  private isServerAndBaseDomainSet(): boolean {
    return (
      isPlatformServer(this.platformId) &&
      !!this.environment.api.homr?.baseDomain?.[this.chain.chainIdentifierAbbr]
    );
  }
}
